/**
 * Board → film: the first cut of a Studio production, as a composition the
 * existing timeline editor opens and the vendor then tweaks.
 *
 * The board is the plan, so this is deliberately literal: one clip per shot in
 * board order, hard cuts (the reference reels cut, they do not dissolve), the
 * shot's own words on screen for exactly as long as the shot. No template
 * pacing or end card is invented here — anything the board did not ask for is
 * one click away in the editor, whereas anything added here has to be found
 * and deleted.
 *
 * Pure, like composition.ts, so it is unit-tested directly.
 */
import {
    ASPECT_SIZES,
    AspectRatio,
    ImageLayer,
    MainClip,
    MotionType,
    OPENER,
    OverlayLayer,
    TextLayer,
    TextShadow,
    LayerAnim,
    VideoComposition,
    addAudioClip,
    addOverlayLayer,
    createAudioClip,
    createColorClip,
    createEmptyComposition,
    createId,
    createMainClip,
    insertMainClips,
    itemEnd,
    mainTrack,
    normalizeComposition,
    round,
    totalDuration,
} from '../content-studio/video-editor/composition';
import type { CameraMove, ShotText, StudioBoard, StudioFormat, StudioShot } from './studio-contract';
import { Graphic, GraphicLogo, GraphicText, PartAnim, graphicsOf } from './graphics';

export interface FilmOptions {
    brand?: { fontFamily?: string; colors?: string[]; logoUrl?: string; name?: string };
    musicUrl?: string;
    musicDurationSec?: number;
    /** How bright the picture is under each text part (0 dark – 1 white), by partLumaKey. */
    luma?: Record<string, number>;
    /** How bright the vendor's logo itself is (its opaque pixels), 0–1. */
    logoLuma?: number;
}

/** The key a part's measured brightness is filed under. */
export function partLumaKey(shotId: string, g: Graphic): string {
    return `${shotId}|${partKey(g)}`;
}

/**
 * What a film is built from: its shots, in order, with their pictures, words
 * and timing. A cut built from an older board is stale — Ardi added captions,
 * and "Make the film" kept opening the cut without them (13 Oct 2026).
 */
export function filmSignature(board: StudioBoard): string {
    const parts = (board?.shots || []).map((s) => [
        s.id, s.kind, s.inFilm === false ? 0 : 1, s.still?.url || '', s.clip?.url || '', s.take3d?.url || '',
        s.durationSec ?? '', s.transition ?? '', s.background ?? '', JSON.stringify(s.graphics || []), s.text?.content || '',
    ]);
    return hashString(JSON.stringify([board?.format, parts]));
}

/** A short, stable hash of a string (djb2), for telling two versions apart. */
export function hashString(text: string): string {
    let h = 5381;
    for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
}

/** Relative luminance of a #hex colour, 0–1. */
function hexLuma(hex: string): number | undefined {
    const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!m) return undefined;
    const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The box a text part takes on the frame, as fractions of it (shared by the layer and the brightness check). */
export function textBox(g: GraphicText, aspect: AspectRatio): { x: number; y: number; width: number; height: number } {
    const { width: W, height: H } = ASPECT_SIZES[aspect];
    const lines = g.text.replace(/\r/g, '').trim().split('\n');
    const fontSize = g.size ?? 0.03;
    const longest = Math.max(...lines.map((l) => Array.from(l).length), 1);
    const em = charWidth(g) + Math.max(0, (g.spacing ?? 0) / 1000);
    // A box a little wider than the longest line, so the words never wrap where the reference did not.
    const width = Math.min(0.98, ((longest * em * fontSize * H) / W) * 1.1 + 0.02);
    const align = g.align || 'left';
    let x = align === 'center' ? g.x : align === 'right' ? g.x - width / 2 : g.x + width / 2;
    x = Math.min(1 - width / 2, Math.max(width / 2, x));
    return { x, y: g.y, width, height: fontSize * lines.length * 1.1 };
}

const FORMAT_TO_ASPECT: Record<StudioFormat, AspectRatio> = {
    '9:16': '9:16',
    '4:5': '4:5',
    '1:1': '1:1',
    '16:9': '16:9',
};

/** A still holds long enough to read, short enough to keep a reel's rhythm. */
const IMAGE_SECONDS = 2.5;
/** A generated clip whose length nobody recorded: the common model output. */
const UNKNOWN_VIDEO_SECONDS = 4;
/** Ken Burns on stills: enough that the frame breathes, not enough to notice. */
const STILL_MOTION_INTENSITY = 0.3;

const SERIF = 'Cormorant Garamond';
const SANS = 'Inter';
const WHITE = '#FFFFFF';
// Same shadow the editor's presets use, so a film caption and a preset caption match.
const SOFT_SHADOW: TextShadow = { color: '#000000', opacity: 0.35, blur: 0.35, offsetY: 0.04 };

/**
 * The editor's safe-area guides (PreviewStage): on a reel the platform covers
 * the top, a deep band at the bottom and the action column on the right.
 * Mirrored here so a film never puts text where the editor flags it red.
 */
const REEL_SAFE = { top: 0.12, bottom: 0.22, left: 0.06, right: 0.14 };
const TITLE_SAFE = { top: 0.08, bottom: 0.08, left: 0.08, right: 0.08 };
/** Air between a text block and the edge of the safe area. */
const SAFE_PAD = 0.01;

type Role = ShotText['role'];
type Position = NonNullable<ShotText['position']>;

/**
 * Per-role type. Sizes are fractions of the frame's SHORT side (as in
 * presets.ts), so a title has the same weight in 9:16 as in 16:9; on 9:16
 * that makes a title ≈6.8% of the height, a caption ≈3.6%, a label ≈3%.
 */
const ROLE_TYPE: Record<Role, {
    size: number;
    weight: number;
    lineHeight: number;
    letterSpacing: number;
    uppercase: boolean;
    defaultFont: 'serif' | 'sans';
    defaultPosition: Position;
    defaultAnim: LayerAnim;
    animSeconds: number;
    /** Portrait block width (fraction of frame width) before safe-area clamping. */
    width: number;
}> = {
    title: {
        size: 0.12, weight: 300, lineHeight: 1.05, letterSpacing: -0.01, uppercase: false,
        defaultFont: 'serif', defaultPosition: 'center', defaultAnim: 'rise', animSeconds: 1.0, width: 0.84,
    },
    caption: {
        size: 0.064, weight: 400, lineHeight: 1.3, letterSpacing: 0.01, uppercase: false,
        defaultFont: 'sans', defaultPosition: 'bottom', defaultAnim: 'fade', animSeconds: 0.6, width: 0.8,
    },
    label: {
        size: 0.054, weight: 500, lineHeight: 1.2, letterSpacing: 0.22, uppercase: true,
        defaultFont: 'sans', defaultPosition: 'bottom-right', defaultAnim: 'fade', animSeconds: 0.5, width: 0.5,
    },
};

const TEXT_ANIM: Record<NonNullable<ShotText['animation']>, LayerAnim> = {
    none: 'none',
    fade: 'fade',
    rise: 'rise',
    type: 'typewriter',
};

// pan/tracking/orbit have no true equivalent on a still; a lateral drift reads closest.
const STILL_MOTION: Record<CameraMove, MotionType> = {
    // A static shot holds still: in a montage of photos the type moves, not the picture.
    static: 'none',
    'push-in': 'zoom-in',
    'pull-out': 'zoom-out',
    pan: 'pan-left',
    orbit: 'pan-right',
    tilt: 'pan-up',
    'crane-down': 'pan-up',
    tracking: 'pan-left',
    handheld: 'zoom-in',
};

/**
 * Turn a Studio board into an editable composition.
 *
 * A shot plays its clip when the clip is ready, else its still, else it is
 * left out — the film is always playable, whatever is still rendering.
 */
/** The editor's frame for a board's format. */
export function filmAspect(board: StudioBoard): AspectRatio {
    return FORMAT_TO_ASPECT[board?.format] ?? '9:16';
}

export function boardToComposition(board: StudioBoard, opts: FilmOptions = {}): VideoComposition {
    const aspect = filmAspect(board);
    const musicUrl = opts.musicUrl || board?.edit?.musicUrl;
    let comp = createEmptyComposition(aspect, 30);

    const placed: Array<{ shot: StudioShot; clip: MainClip }> = [];
    for (const shot of board?.shots || []) {
        // A key photo made only to derive the others from is not part of the film.
        if (shot.inFilm === false) continue;
        const clip = clipForShot(shot, placed.length === 0, !!musicUrl);
        if (clip) placed.push({ shot, clip });
    }
    comp = insertMainClips(comp, placed.map((p) => p.clip));
    // insertMainClips lays the track out; read the clips back for their real starts.
    const laid = mainTrack(comp).clips;

    let previous: { text: ShotText; layer: TextLayer } | null = null;
    const layers: TextLayer[] = [];
    // Words and logos set as parts — the reference's own typography, timed —
    // are laid out where the shot puts them; a shot that has them is not also
    // given the generic title.
    let carried = new Map<string, OverlayLayer>();
    const partLayers: OverlayLayer[] = [];
    placed.forEach(({ shot }, i) => {
        const clip = laid[i];
        const parts = clip ? graphicsOf(shot).filter((g) => (g.type === 'text' && g.text.trim()) || g.type === 'logo') : [];
        if (clip && parts.length) {
            const next = new Map<string, OverlayLayer>();
            for (const g of parts) {
                const key = partKey(g);
                const held = carried.get(key);
                // The same words in the same place across a cut are one line
                // that stays, not a line that re-animates at every cut.
                if (held && !g.at && Math.abs(itemEnd(held) - clip.start) < 1e-3) {
                    held.duration = round(itemEnd(clip) - held.start);
                    next.set(key, held);
                    continue;
                }
                const layer = partLayer(g, shot, clip, aspect, opts.brand, opts.luma?.[partLumaKey(shot.id, g)], opts.logoLuma);
                if (!layer) continue;
                partLayers.push(layer);
                next.set(key, layer);
            }
            carried = next;
            previous = null;
            return;
        }
        carried = new Map();
        const text = shot.text?.content?.trim() ? shot.text : undefined;
        if (!clip || !text) {
            previous = null;
            return;
        }
        // The same words over consecutive cuts are one line that stays put, not
        // a line that re-animates at every cut.
        if (previous && sameText(previous.text, text) && Math.abs(itemEnd(previous.layer) - clip.start) < 1e-3) {
            previous.layer.duration = round(itemEnd(clip) - previous.layer.start);
            return;
        }
        const layer = textLayer(text, aspect, clip.start, clip.duration, opts.brand);
        layers.push(layer);
        previous = { text, layer };
    });
    // With hard cuts the layers abut, so they all land on the one overlay track.
    comp = layers.reduce((acc, l) => addOverlayLayer(acc, finishAnim(l)), comp);
    // Parts overlap within a shot; the editor puts each on a track where it fits.
    comp = partLayers.reduce((acc, l) => addOverlayLayer(acc, finishAnim(l)), comp);

    if (musicUrl && laid.length) {
        const total = totalDuration(comp);
        // No looping in the editor: a short track simply ends early (and fades there).
        const duration = opts.musicDurationSec ? Math.min(opts.musicDurationSec, total) : total;
        const audio = createAudioClip({ src: musicUrl, duration, sourceDuration: opts.musicDurationSec });
        // The cut and the beat start together on frame one; only the tail fades.
        comp = addAudioClip(comp, { ...audio, fadeIn: 0, fadeOut: Math.min(1, duration / 2) });
    }
    return normalizeComposition(comp);
}

function isReady(stage: StudioShot['still']): boolean {
    return !!stage && stage.status === 'ready' && !!stage.url;
}

function clipForShot(shot: StudioShot, isFirst: boolean, underMusic: boolean): MainClip | null {
    if (!shot) return null;
    // The shot's own way in; a hard cut is no transition. The first frame is the
    // platform's cover, so unless the shot asks otherwise it opens on the picture.
    const own = shot.transition && shot.transition !== 'cut' ? { type: shot.transition, duration: TRANSITION_SECONDS[shot.transition] ?? 0.4 } : undefined;
    const transitionIn = own ?? (isFirst && shot.transition !== 'cut' ? OPENER : undefined);
    // A card is a colour and its words: no media at all.
    if (shot.kind === 'card') {
        return createColorClip({ color: shot.background || '#0B0B0B', duration: positive(shot.durationSec) ?? 2, transitionIn, label: shot.title });
    }
    // A photo shot plays its photo even if a clip was once made of it: the
    // vendor turned it back into a photo for a reason (a montage of stills).
    if (shot.kind === 'video' && isReady(shot.clip)) {
        const source = positive(shot.clip.durationSec);
        const duration = positive(shot.durationSec) ?? source ?? UNKNOWN_VIDEO_SECONDS;
        // Keep the middle of a longer clip: a generated move settles at the
        // start and coasts at the end, the middle is where it travels.
        const trimIn = source && source > duration ? (source - duration) / 2 : 0;
        const clip = createMainClip({
            kind: 'video',
            src: shot.clip.url,
            assetUuid: shot.clip.assetUuid,
            thumbnailUrl: isReady(shot.still) ? shot.still.url : undefined,
            duration,
            sourceDuration: source,
            trimIn: round(trimIn),
            transitionIn,
            label: shot.title,
        });
        // A generated clip's own sound (room tone, model hiss) only muddies a music bed.
        return underMusic ? { ...clip, muted: true } : clip;
    }
    if (isReady(shot.still)) {
        // Words Studio drew on the photo are animated by the film instead, so it
        // takes the clean photo — the same title twice otherwise.
        const drawn = !!shot.design?.auto && shot.design.assetUuid === shot.still.assetUuid;
        const src = drawn ? shot.design!.backgroundUrl : shot.still.url;
        // A photo shot with no move asked for holds still (the type carries the
        // motion); a video shot's photo, standing in for its clip, keeps a slow move.
        const motion = shot.motion ? STILL_MOTION[shot.motion] ?? 'zoom-in' : shot.kind === 'image' ? 'none' : 'zoom-in';
        return createMainClip({
            kind: 'image',
            src,
            assetUuid: drawn ? shot.design!.backgroundAssetUuid : shot.still.assetUuid,
            thumbnailUrl: src,
            duration: positive(shot.durationSec) ?? IMAGE_SECONDS,
            motion: { type: motion, intensity: motion === 'none' ? 0 : STILL_MOTION_INTENSITY },
            transitionIn,
            label: shot.title,
        });
    }
    return null;
}

function positive(v: number | undefined): number | undefined {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}

function sameText(a: ShotText, b: ShotText): boolean {
    return a.content.trim() === b.content.trim()
        && a.role === b.role
        && (a.position || '') === (b.position || '')
        && (a.font || '') === (b.font || '')
        && (a.animation || '') === (b.animation || '');
}

/** Short-side fraction → the composition's height-relative unit. */
function sizeFor(aspect: AspectRatio, shortSide: number): number {
    const { width, height } = ASPECT_SIZES[aspect];
    return (shortSide * Math.min(width, height)) / height;
}

function fontFamilyFor(text: ShotText, brand: FilmOptions['brand']): string {
    const font = text.font || ROLE_TYPE[text.role].defaultFont;
    if (font === 'brand') return brand?.fontFamily?.trim() || (ROLE_TYPE[text.role].defaultFont === 'serif' ? SERIF : SANS);
    return font === 'serif' ? SERIF : SANS;
}

/**
 * Rough line count, so a block that wraps is still placed inside the safe
 * area. The renderer measures for real; this only needs to err long.
 */
function estimateLines(text: string, fontSize: number, widthFrac: number, aspect: AspectRatio, uppercase: boolean, letterSpacing: number): number {
    const { width, height } = ASPECT_SIZES[aspect];
    const em = (uppercase ? 0.66 : 0.52) + Math.max(0, letterSpacing);
    const perLine = Math.max(1, Math.floor((widthFrac * width) / (em * fontSize * height)));
    return text.split('\n').reduce((n, para) => n + Math.max(1, Math.ceil(Array.from(para.trim()).length / perLine)), 0);
}

function textLayer(text: ShotText, aspect: AspectRatio, start: number, duration: number, brand: FilmOptions['brand']): TextLayer {
    const role = ROLE_TYPE[text.role] ? text.role : 'caption';
    const type = ROLE_TYPE[role];
    const position: Position = text.position || type.defaultPosition;
    const safe = aspect === '9:16' ? REEL_SAFE : TITLE_SAFE;
    const { width: W, height: H } = ASPECT_SIZES[aspect];
    const fontSize = sizeFor(aspect, type.size);

    // Landscape: the same physical measure is a smaller share of the width.
    const measure = W > H ? Math.min(0.9, type.width * (H / W) * 1.4) : type.width;
    const align: TextLayer['align'] = position === 'bottom-right' ? 'right' : position === 'bottom-left' ? 'left' : 'center';
    let width: number;
    let x: number;
    if (align === 'center') {
        // Centred on the frame, so both sides must clear the wider margin.
        width = Math.min(measure, 1 - 2 * Math.max(safe.left, safe.right));
        x = 0.5;
    } else {
        width = Math.min(measure, 1 - safe.left - safe.right);
        x = align === 'right' ? 1 - safe.right - width / 2 : safe.left + width / 2;
    }

    const content = text.content.trim();
    const lines = estimateLines(content, fontSize, width, aspect, type.uppercase, type.letterSpacing);
    const half = (lines * fontSize * type.lineHeight) / 2;
    let y: number;
    if (position === 'center') y = 0.5;
    else if (position === 'top') y = safe.top + SAFE_PAD + half;
    else y = 1 - safe.bottom - SAFE_PAD - half;
    // A block too tall for its band still must not leave the safe area at the top.
    y = Math.max(safe.top + half, Math.min(1 - safe.bottom - half, y));

    const animIn = text.animation ? TEXT_ANIM[text.animation] ?? type.defaultAnim : type.defaultAnim;
    return {
        id: createId('ly'),
        kind: 'text',
        start: round(start),
        duration: round(duration),
        x: round(x),
        y: round(y),
        width: round(width),
        opacity: 1,
        rotation: 0,
        animIn,
        // A hard-cut film: the words leave with the picture, they do not fade early.
        animOut: 'none',
        animDuration: animIn === 'typewriter' ? 1.4 : type.animSeconds,
        text: content,
        align,
        fontFamily: fontFamilyFor(text, brand),
        fontSize,
        fontWeight: type.weight,
        fontStyle: 'normal',
        letterSpacing: type.letterSpacing,
        lineHeight: type.lineHeight,
        color: WHITE,
        uppercase: type.uppercase,
        // Small sans over moving footage needs a touch more separation than a display title.
        shadow: role === 'title' ? SOFT_SHADOW : { ...SOFT_SHADOW, opacity: 0.5 },
    };
}

/**
 * The renderer caps an animation at half the layer, but a 2.5 s shot whose
 * line spends 1 s arriving is read for barely a second: keep arrivals to a
 * third of the time on screen (typing gets a little more, it is the reading).
 */
function finishAnim<T extends OverlayLayer>(layer: T): T {
    const share = TYPING.includes(layer.animIn) ? 0.45 : 0.33;
    return { ...layer, animDuration: round(Math.max(0.2, Math.min(layer.animDuration, layer.duration * share))) };
}

// ── parts: the reference's own typography, timed ─────────────────────

/** How long a transition into a shot takes, by kind. */
const TRANSITION_SECONDS: Partial<Record<NonNullable<StudioShot['transition']>, number>> = {
    blur: 0.6, iris: 0.6, flash: 0.3, whip: 0.35, 'fade-black': 0.6, 'fade-white': 0.5, crossfade: 0.5,
};

/** Arrivals that read letter by letter or word by word — they get more of the time on screen. */
const TYPING: LayerAnim[] = ['typewriter', 'letter-stagger', 'word-stagger'];

const PART_FONT = { serif: SERIF, sans: SANS, mono: 'DM Mono' } as const;

/** A part's identity across cuts: same words, same face, same place. */
function partKey(g: Graphic): string {
    if (g.type === 'logo') return `logo|${g.x.toFixed(2)}|${g.y.toFixed(2)}`;
    if (g.type !== 'text') return '';
    return [g.text.trim(), g.family || g.font || '', g.x.toFixed(2), g.y.toFixed(2), g.size || ''].join('|');
}

function partAnim(a: PartAnim | undefined, fallback: LayerAnim): LayerAnim {
    return (a as LayerAnim | undefined) ?? fallback;
}

/** Seconds an arrival takes: typing scales with the words, a logo reveal takes its time. */
function partAnimSeconds(anim: LayerAnim, text: string): number {
    if (TYPING.includes(anim)) return Math.max(0.6, Math.min(1.8, Array.from(text).length * 0.05));
    if (anim === 'tracking-in') return 1.2;
    if (anim === 'mask-up') return 0.8;
    return 0.6;
}

/** Rough width of a line in a face, as a fraction of the font size per character. */
function charWidth(g: GraphicText): number {
    const f = g.family || '';
    if (/Allura|Pinyon/.test(f)) return 0.4;
    if (/Bebas/.test(f)) return 0.42;
    if (/Archivo Black/.test(f)) return 0.72;
    if (/Italiana|Syne/.test(f)) return g.upper ? 0.74 : 0.56;
    return g.upper ? 0.66 : 0.52;
}

/** One part as an editor layer: where the shot puts it, for when it says, in the face it names. */
function partLayer(g: Graphic, shot: StudioShot, clip: MainClip, aspect: AspectRatio, brand: FilmOptions['brand'], luma?: number, logoLuma?: number): OverlayLayer | null {
    const { width: W, height: H } = ASPECT_SIZES[aspect];
    const clipEnd = itemEnd(clip);
    const at = Math.min(Math.max(0, Number(g.at) || 0), Math.max(0, clip.duration - 0.2));
    const start = clip.start + at;
    const end = g.dur ? Math.min(clipEnd, start + g.dur) : clipEnd;
    if (end - start < 0.15) return null;
    const onPicture = shot.kind !== 'card';

    if (g.type === 'logo') {
        const logo = g as GraphicLogo;
        const animIn = partAnim(logo.anim, 'fade');
        // A logo the card's colour disappears into it — a black logo on the
        // reference's black end card (13 Oct 2026): set the name instead.
        const cardLuma = !onPicture ? hexLuma(shot.background || '#0B0B0B') : undefined;
        const lost = cardLuma !== undefined && logoLuma !== undefined && Math.abs(cardLuma - logoLuma) < 0.3 && !!brand?.name?.trim();
        if (brand?.logoUrl && !lost) {
            const layer: ImageLayer = {
                id: createId('ly'), kind: 'image', src: brand.logoUrl,
                start: round(start), duration: round(end - start),
                x: round(logo.x), y: round(logo.y), width: round(logo.w ?? 0.3), opacity: 1, rotation: 0,
                animIn, animOut: partAnim(logo.out, 'none'), animDuration: animIn === 'tracking-in' ? 1.2 : 0.7,
            };
            return layer;
        }
        // No logo uploaded: the brand's name, set as a quiet wordmark.
        const name = brand?.name?.trim();
        if (!name) return null;
        const nameColor = cardLuma !== undefined ? (cardLuma > 0.5 ? '#1C1917' : WHITE) : logo.color;
        return partLayer({ type: 'text', text: name, x: logo.x, y: logo.y, align: 'center', size: Math.max(0.025, (logo.w ?? 0.3) * (W / H) * 0.16), family: 'Montserrat', weight: 500, upper: true, spacing: 180, color: nameColor, anim: logo.anim, out: logo.out }, shot, clip, aspect, brand);
    }
    if (g.type !== 'text') return null;

    const text = g.text.replace(/\r/g, '').trim();
    const fontSize = g.size ?? 0.03;
    const align = g.align || 'left';
    const { x, width } = textBox(g, aspect);
    const animIn = partAnim(g.anim, 'fade');
    // The reference's colours stay (white stays white); where our picture is
    // lighter under the words than the reference's was, a deeper shadow keeps
    // them readable — white captions vanished on a pale wall (13 Oct 2026).
    // Dark words over a dark or mid-tone part of our picture (a veined tile is
    // busier than its average says) cannot be saved by a shadow:
    // they turn white (navy type on a grey veined tile, same night).
    const darkOnDark = onPicture && (hexLuma(g.color || WHITE) ?? 1) < 0.35 && luma !== undefined && luma < 0.45;
    const color = darkOnDark ? WHITE : g.color || WHITE;
    const lightType = (hexLuma(color) ?? 1) > 0.6;
    const shadow: TextShadow | undefined = !onPicture || g.outline ? undefined
        : lightType && luma !== undefined && luma > 0.6
            ? { color: '#000000', opacity: luma > 0.75 ? 0.62 : 0.48, blur: 0.55, offsetY: 0.03 }
            : { ...SOFT_SHADOW, opacity: 0.28 };
    const layer: TextLayer = {
        id: createId('ly'),
        kind: 'text',
        start: round(start),
        duration: round(end - start),
        x: round(x),
        y: round(g.y),
        width: round(width),
        opacity: 1,
        rotation: 0,
        animIn,
        animOut: partAnim(g.out, 'none'),
        animDuration: partAnimSeconds(animIn, text),
        text,
        align,
        fontFamily: g.family || PART_FONT[g.font || 'sans'],
        fontSize,
        // Archivo Black is one heavy weight; asking for more only falls back.
        fontWeight: g.family === 'Archivo Black' ? 400 : g.weight ?? 400,
        fontStyle: g.italic ? 'italic' : 'normal',
        letterSpacing: (g.spacing ?? 0) / 1000,
        lineHeight: 1.1,
        color,
        uppercase: !!g.upper,
        // Over a picture, a touch of shadow keeps white type readable; on a card it would only blur it.
        shadow,
        ...(g.outline ? { stroke: { color, width: 0.03 }, fillOpacity: 0 } : {}),
    };
    return layer;
}
